// libraries
import React, { useEffect, useState } from 'react'
import debounce from 'lodash.debounce'

import { BarLoader, Divider } from '@st-operations/uikit/src/v2/index.js'
import Events from '@st-operations/uikit/Events'

import DraggableList from './DraggableList.jsx'
import TimeIntervalOptions from './TimeIntervalOptions.jsx'

import './SelectedTopicsEditor.scss'
import Select from '_components/select/index.jsx'
import { pretty } from './helpers/pretty.js'
import { searchTopic } from '_services/insights-v2.js'

const formatTopics = (raw) =>
  raw
    ?.map(({ term, size }) => ({
      label: pretty(term),
      value: pretty(term),
      size,
    }))
    ?.sort((a, b) => b.size - a.size)

const debouncedSearchTopic = debounce(
  async ({ term, audienceId }, { setTopicsOptions, setLoading }) => {
    if (!term || !audienceId) {
      return
    }

    try {
      // setup
      setLoading(true)
      setTopicsOptions([])

      // fetch
      const result = await searchTopic({ term, audienceId })
      setTopicsOptions(formatTopics(result))
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  },
  500
)

const SelectedTopicsEditor = ({ field, slide, index, onFieldChange, constraint, audienceId }) => {
  const { data, template, options } = slide
  const [topicsOptions, setTopicsOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const selectedEntities = data?.selected_entities ?? []

  // Handle updates on selected entities' drag list
  useEffect(() => {
    // setListOrder is the event emitted by DraggableList
    const event = Events.addListener('setListOrder', (newData) => {
      if (newData?.field && newData?.list) {
        // save data in global object to sent to BE when saving
        onFieldChange({
          index: newData.index,
          value: newData.list,
          type: 'selected_entities',
          field: newData.field,
        })
      }
    })

    return () => event.remove()
  }, [])

  const handleInputKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTopic(e.target.value)
    }
  }

  // merge new item into selected_entities list and uses callback to add to root object
  const addTopic = (value) => {
    if (value.trim() === '') return
    const newSelectedEntities = [...selectedEntities, value]
    onFieldChange({ index, value: newSelectedEntities, type: field, field })
  }

  const removeTopic = ({ currentTarget }) => {
    // `value` can be either a topic' object or a string
    const value = JSON.parse(currentTarget.dataset.value)

    const newEntities =
      typeof value === 'string'
        ? selectedEntities.filter((entity) => entity !== value)
        : selectedEntities.filter((item) => item['_id'] !== value['_id'])

    onFieldChange({ index, value: newEntities, type: 'selected_entities', field })
  }

  // NOTE: when user types
  const onInputChange = (query) => {
    debouncedSearchTopic({ term: query, audienceId }, { setTopicsOptions, setLoading })
  }

  // NOTE: when user selects
  const onChange = (item, { action }) => {
    if (action === 'select-option' && item) {
      addTopic(item.value)
    }
  }

  const noOptionsMessage = () => {
    return loading ? 'Loading...' : 'Type to search'
  }

  // modify select labels
  const itemType = data[field]?.type ?? data['section_1']?.type

  return (
    <div className="selected-entities-editor">
      <Divider className="selected-entities-editor__divider" />
      <TimeIntervalOptions
        index={index}
        timeIntervalOptions={options}
        onFieldChange={onFieldChange}
        timeRange={data.time_range}
        timeFrame={data.time_frame}
        yLabels={data.y_labels}
      />
      <If condition={constraint && constraint.min && constraint.max}>
        <h5>{`Note: You need to add at least ${constraint.min}
              and no more than ${constraint.max} ${itemType.toLowerCase()}`}</h5>
      </If>
      <Select
        label="Topics"
        placeholder={
          selectedEntities?.length >= constraint?.max
            ? 'Limit of topics reached'
            : 'eg: ShareThis, Facebook'
        }
        options={topicsOptions}
        onChange={onChange}
        onInputChange={onInputChange}
        isDisabled={selectedEntities?.length >= constraint?.max}
        isSearchable={true}
        noOptionsMessage={noOptionsMessage}
        onKeyDown={handleInputKeydown}
      />
      <If condition={loading}>
        <BarLoader containerStyle={{ left: 0, position: 'relative' }} />
      </If>
      <div style={{ marginBottom: '20px' }}>
        <DraggableList
          template={template}
          label={`${itemType} Order:`}
          description="Drag and drop items to change order"
          slideIndex={index}
          format={(data.insights && data.insights[0][1]) || 'percent'}
          onRemove={removeTopic}
          shown={[...selectedEntities]}
          shown_field={field ?? 'selected_entities'}
        />
      </div>
    </div>
  )
}
// export
export default SelectedTopicsEditor
